import { gql, useQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import { AlertTriangle, FileSearch, Shield, ShieldAlert } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const GET_RESOLVER_POLICY_REPORT = gql`
  query GetResolverPolicyReport {
    getResolverPolicyReport
  }
`;

type Severity = 'critical' | 'high' | 'medium';
type EndpointSeverity = Severity | 'ok';
type ResolverKind = 'query' | 'mutation' | 'subscription';
type TypeFieldAction = 'read' | 'write';

type ReportEndpoint = {
  type: ResolverKind;
  name: string;
  file: string;
  line: number;
  policies: string[];
  unauthenticated: boolean;
  mounted: boolean;
  issueCodes: string[];
  highestSeverity: EndpointSeverity;
  recommendedPolicies: string[];
};

type ReportTypeField = {
  typeName: string;
  fieldName: string;
  file: string;
  line: number;
  policies: string[];
  input: boolean;
  action: TypeFieldAction;
  mounted: boolean;
  issueCodes: string[];
  highestSeverity: EndpointSeverity;
  recommendedPolicies: string[];
};

type PolicyGroup = {
  policy: string;
  endpointCount: number;
  issueCount: number;
  endpoints: Array<{
    type: ResolverKind;
    name: string;
    file: string;
    line: number;
    mounted: boolean;
    issueCodes: string[];
  }>;
};

type ReportSummary = {
  totalEndpoints: number;
  mountedEndpoints: number;
  sourceOnlyEndpoints: number;
  totalTypeFields: number;
  totalTypeIssues: number;
  totalIssues: number;
  kindCounts: Record<string, number>;
  typeActionCounts: Record<string, number>;
  severityCounts: Record<string, number>;
  issueCounts: Record<string, number>;
  namespaceCounts: Record<string, number>;
  generatedAt: string;
};

type ResolverPolicyReportPayload = {
  summary: ReportSummary;
  endpoints: ReportEndpoint[];
  typeFields: ReportTypeField[];
  issues: unknown[];
  typeFieldIssues: unknown[];
  policyGroups: PolicyGroup[];
};

type ResolverPolicyReportViewProps = {
  embedded?: boolean;
  title?: string;
  description?: string;
};

const emptyPayload: ResolverPolicyReportPayload = {
  summary: {
    totalEndpoints: 0,
    mountedEndpoints: 0,
    sourceOnlyEndpoints: 0,
    totalTypeFields: 0,
    totalTypeIssues: 0,
    totalIssues: 0,
    kindCounts: {},
    typeActionCounts: {},
    severityCounts: {},
    issueCounts: {},
    namespaceCounts: {},
    generatedAt: ''
  },
  endpoints: [],
  typeFields: [],
  issues: [],
  typeFieldIssues: [],
  policyGroups: []
};

const parsePayload = (raw?: string | null): ResolverPolicyReportPayload => {
  if (!raw) {
    return emptyPayload;
  }

  try {
    const parsed = JSON.parse(raw);
    return {
      summary: parsed?.summary || emptyPayload.summary,
      endpoints: Array.isArray(parsed?.endpoints) ? parsed.endpoints : [],
      typeFields: Array.isArray(parsed?.typeFields) ? parsed.typeFields : [],
      issues: Array.isArray(parsed?.issues) ? parsed.issues : [],
      typeFieldIssues: Array.isArray(parsed?.typeFieldIssues) ? parsed.typeFieldIssues : [],
      policyGroups: Array.isArray(parsed?.policyGroups) ? parsed.policyGroups : []
    };
  } catch (error) {
    return emptyPayload;
  }
};

const severityBadgeClass = (severity: EndpointSeverity) => {
  if (severity === 'critical') {
    return 'border-red-200 bg-red-50 text-red-700';
  }
  if (severity === 'high') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  if (severity === 'medium') {
    return 'border-blue-200 bg-blue-50 text-blue-700';
  }
  return 'border-emerald-200 bg-emerald-50 text-emerald-700';
};

const sourceOnlyBadgeClass = () => {
  return 'border-slate-200 bg-slate-50 text-slate-700';
};

const issueBadgeClass = (count: number) => {
  if (count > 0) {
    return 'border-red-200 bg-red-50 text-red-700';
  }
  return 'border-emerald-200 bg-emerald-50 text-emerald-700';
};

const formatFileDisplay = (file: string, line: number) => {
  const trimmedFile = file.replace(/\/schema\.ts$/, '');
  if (line < 1) {
    return trimmedFile;
  }
  return `${trimmedFile}:${line}`;
};

const kindBadgeClass = (kind: ResolverKind) => {
  if (kind === 'query') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (kind === 'mutation') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  return 'border-purple-200 bg-purple-50 text-purple-700';
};

const typeModeBadgeClass = (input: boolean) => {
  if (input) {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }
  return 'border-emerald-200 bg-emerald-50 text-emerald-700';
};

export const ResolverPolicyReportView = ({
  embedded = false,
  title = 'Security Feature Report',
  description
}: ResolverPolicyReportViewProps) => {
  const { data, loading, error } = useQuery<{ getResolverPolicyReport: string }>(GET_RESOLVER_POLICY_REPORT, {
    fetchPolicy: 'cache-and-network'
  });
  const payload = useMemo(() => parsePayload(data?.getResolverPolicyReport), [data?.getResolverPolicyReport]);
  const [activeTab, setActiveTab] = useState<'resolvers' | 'types' | 'policies'>('resolvers');
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<'all' | ResolverKind>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | EndpointSeverity>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeModeFilter, setTypeModeFilter] = useState<'all' | 'input' | 'output'>('all');
  const [typeActionFilter, setTypeActionFilter] = useState<'all' | TypeFieldAction>('all');
  const [typeSeverityFilter, setTypeSeverityFilter] = useState<'all' | EndpointSeverity>('all');
  const [typeStatusFilter, setTypeStatusFilter] = useState<string>('all');

  const severityCounts = useMemo(() => {
    return payload.endpoints.reduce<Record<string, number>>((counts, endpoint) => {
      counts[endpoint.highestSeverity] = (counts[endpoint.highestSeverity] || 0) + 1;
      return counts;
    }, {});
  }, [payload.endpoints]);

  const statusCounts = useMemo(() => {
    return payload.endpoints.reduce<Record<string, number>>((counts, endpoint) => {
      if (endpoint.unauthenticated) {
        counts.unauthenticated = (counts.unauthenticated || 0) + 1;
      }
      if (endpoint.issueCodes.length === 0) {
        counts.ok = (counts.ok || 0) + 1;
        return counts;
      }

      endpoint.issueCodes.forEach((issueCode) => {
        counts[issueCode] = (counts[issueCode] || 0) + 1;
      });
      return counts;
    }, {});
  }, [payload.endpoints]);

  const typeSeverityCounts = useMemo(() => {
    return payload.typeFields.reduce<Record<string, number>>((counts, field) => {
      counts[field.highestSeverity] = (counts[field.highestSeverity] || 0) + 1;
      return counts;
    }, {});
  }, [payload.typeFields]);

  const typeStatusCounts = useMemo(() => {
    return payload.typeFields.reduce<Record<string, number>>((counts, field) => {
      if (field.issueCodes.length === 0) {
        counts.ok = (counts.ok || 0) + 1;
        return counts;
      }

      field.issueCodes.forEach((issueCode) => {
        counts[issueCode] = (counts[issueCode] || 0) + 1;
      });
      return counts;
    }, {});
  }, [payload.typeFields]);

  const statusFilterOptions = useMemo(() => {
    return Object.entries(statusCounts)
      .filter(([issueCode]) => issueCode !== 'ok')
      .sort((left, right) => right[1] - left[1])
      .map(([issueCode]) => issueCode);
  }, [statusCounts]);

  const typeStatusFilterOptions = useMemo(() => {
    return Object.entries(typeStatusCounts)
      .filter(([issueCode]) => issueCode !== 'ok')
      .sort((left, right) => right[1] - left[1])
      .map(([issueCode]) => issueCode);
  }, [typeStatusCounts]);

  const filteredEndpoints = useMemo(() => {
    const query = search.trim().toLowerCase();

    return payload.endpoints.filter((endpoint) => {
      if (kindFilter !== 'all' && endpoint.type !== kindFilter) {
        return false;
      }
      if (severityFilter !== 'all' && endpoint.highestSeverity !== severityFilter) {
        return false;
      }
      if (statusFilter === 'unauthenticated' && endpoint.unauthenticated !== true) {
        return false;
      }
      if (statusFilter === 'ok' && endpoint.issueCodes.length > 0) {
        return false;
      }
      if (
        statusFilter !== 'all' &&
        statusFilter !== 'ok' &&
        statusFilter !== 'unauthenticated' &&
        !endpoint.issueCodes.includes(statusFilter)
      ) {
        return false;
      }
      if (!query) {
        return true;
      }

      const haystack = [
        endpoint.type,
        endpoint.name,
        endpoint.file,
        ...endpoint.policies,
        ...endpoint.issueCodes,
        ...endpoint.recommendedPolicies
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [kindFilter, payload.endpoints, search, severityFilter, statusFilter]);

  const filteredPolicyGroups = useMemo(() => {
    const query = search.trim().toLowerCase();

    return payload.policyGroups.filter((group) => {
      if (!query) {
        return true;
      }

      const haystack = [
        group.policy,
        ...group.endpoints.map((endpoint) => `${endpoint.type} ${endpoint.name} ${endpoint.file}`)
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [payload.policyGroups, search]);

  const filteredTypeFields = useMemo(() => {
    const query = search.trim().toLowerCase();

    return payload.typeFields.filter((field) => {
      if (typeModeFilter === 'input' && field.input !== true) {
        return false;
      }
      if (typeModeFilter === 'output' && field.input === true) {
        return false;
      }
      if (typeActionFilter !== 'all' && field.action !== typeActionFilter) {
        return false;
      }
      if (typeSeverityFilter !== 'all' && field.highestSeverity !== typeSeverityFilter) {
        return false;
      }
      if (typeStatusFilter === 'ok' && field.issueCodes.length > 0) {
        return false;
      }
      if (
        typeStatusFilter !== 'all' &&
        typeStatusFilter !== 'ok' &&
        !field.issueCodes.includes(typeStatusFilter)
      ) {
        return false;
      }
      if (!query) {
        return true;
      }

      const haystack = [
        field.typeName,
        field.fieldName,
        field.file,
        ...field.policies,
        ...field.issueCodes,
        ...field.recommendedPolicies
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [
    payload.typeFields,
    search,
    typeActionFilter,
    typeModeFilter,
    typeSeverityFilter,
    typeStatusFilter
  ]);

  const titleClass = embedded ? 'text-lg' : 'text-2xl';
  const bodyTextClass = embedded ? 'text-xs' : 'text-sm';
  const summaryValueClass = embedded ? 'text-2xl' : 'text-3xl';
  const filterButtonClass = embedded ? 'rounded-full border px-2.5 py-1 text-[11px] font-medium transition' : 'rounded-full border px-3 py-1.5 text-xs font-medium transition';
  const tableHeaderClass = embedded ? 'text-[11px]' : '';
  const detailBadgeClass = embedded ? 'px-1.5 py-0 text-[10px] leading-4' : '';
  const resolverNameClass = embedded ? 'text-[11px]' : '';
  const codeClass = embedded ? 'block text-[11px] text-slate-700' : 'block text-xs text-slate-700';
  const fileCodeClass = embedded ? 'text-[11px] text-slate-500' : 'text-xs text-slate-500';

  if (loading && !data?.getResolverPolicyReport) {
    return (
      <div className={`flex min-h-[40vh] items-center justify-center ${embedded ? 'p-4' : 'p-8'}`}>
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <Shield className="h-8 w-8 animate-pulse text-slate-700" />
          <p className="text-sm font-medium">Building security policy report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={embedded ? 'p-0' : 'p-8'}>
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-900">
              <AlertTriangle className="h-5 w-5" />
              Security policy report failed
            </CardTitle>
            <CardDescription className="text-red-700">
              The admin report endpoint returned an error.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <code className="block rounded border border-red-200 bg-white px-3 py-2 text-xs text-red-700">
              {error.message}
            </code>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={embedded ? 'flex w-full flex-col gap-4' : 'mx-auto flex w-full max-w-[112rem] flex-col gap-6 p-8'}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className={`${titleClass} font-bold tracking-tight text-slate-900`}>
            {title}
          </h1>
          {description && (
            <p className={`mt-1 max-w-3xl text-slate-600 ${bodyTextClass}`}>{description}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={`w-fit border-slate-200 bg-slate-50 text-slate-700 ${embedded ? 'text-[11px]' : ''}`}>
            {payload.summary.sourceOnlyEndpoints || 0} source-only
          </Badge>
          <Badge variant="outline" className={`w-fit border-slate-200 bg-slate-50 text-slate-700 ${embedded ? 'text-[11px]' : ''}`}>
            {payload.summary.mountedEndpoints || 0} live
          </Badge>
          <Badge variant="outline" className={`w-fit border-slate-200 bg-slate-50 text-slate-700 ${embedded ? 'text-[11px]' : ''}`}>
            Generated {payload.summary.generatedAt ? new Date(payload.summary.generatedAt).toLocaleString() : 'now'}
          </Badge>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4 xl:grid-cols-8">
        <Card className="border-slate-200">
          <CardHeader className="border-b-0 pb-2">
            <CardDescription className={embedded ? 'text-[11px]' : ''}>Resolvers</CardDescription>
            <CardTitle className={summaryValueClass}>{payload.summary.totalEndpoints}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-slate-200">
          <CardHeader className="border-b-0 pb-2">
            <CardDescription className={embedded ? 'text-[11px]' : ''}>Type Fields</CardDescription>
            <CardTitle className={summaryValueClass}>{payload.summary.totalTypeFields || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50">
          <CardHeader className="border-b-0 pb-2">
            <CardDescription className={`text-emerald-700 ${embedded ? 'text-[11px]' : ''}`}>Queries</CardDescription>
            <CardTitle className={`${summaryValueClass} text-emerald-900`}>
              {payload.summary.kindCounts.query || 0}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="border-b-0 pb-2">
            <CardDescription className={`text-amber-700 ${embedded ? 'text-[11px]' : ''}`}>Mutations</CardDescription>
            <CardTitle className={`${summaryValueClass} text-amber-900`}>
              {payload.summary.kindCounts.mutation || 0}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader className="border-b-0 pb-2">
            <CardDescription className={`text-purple-700 ${embedded ? 'text-[11px]' : ''}`}>Subscriptions</CardDescription>
            <CardTitle className={`${summaryValueClass} text-purple-900`}>
              {payload.summary.kindCounts.subscription || 0}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-slate-200">
          <CardHeader className="border-b-0 pb-2">
            <CardDescription className={embedded ? 'text-[11px]' : ''}>Total Issues</CardDescription>
            <CardTitle className={summaryValueClass}>{payload.summary.totalIssues}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="border-b-0 pb-2">
            <CardDescription className={`text-red-700 ${embedded ? 'text-[11px]' : ''}`}>Critical</CardDescription>
            <CardTitle className={`${summaryValueClass} text-red-900`}>
              {severityCounts.critical || 0}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="border-b-0 pb-2">
            <CardDescription className={`text-amber-700 ${embedded ? 'text-[11px]' : ''}`}>High</CardDescription>
            <CardTitle className={`${summaryValueClass} text-amber-900`}>
              {severityCounts.high || 0}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <CardTitle className={`flex items-center gap-2 text-slate-900 ${embedded ? 'text-base' : ''}`}>
              <FileSearch className="h-5 w-5" />
              Report Filters
            </CardTitle>
            <CardDescription className={embedded ? 'text-[11px]' : ''}>
              Read-only review surface. Fix code, restart, and the errors clear from this list.
            </CardDescription>
          </div>
          <div className="w-full max-w-md">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search resolver, type field, feature, issue, or file"
              className={embedded ? 'h-9 text-xs' : ''}
            />
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {activeTab === 'resolvers' ? (
            <>
              <div className="mt-1 flex flex-wrap gap-2">
                {(['all', 'query', 'mutation', 'subscription'] as const).map((value) => {
                  const count = value === 'all' ? payload.summary.totalEndpoints : payload.summary.kindCounts[value] || 0;
                  const active = kindFilter === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setKindFilter(value)}
                      className={`${filterButtonClass} ${
                        active
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {value === 'all' ? 'All Kinds' : value} ({count})
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2">
                {(['all', 'critical', 'high', 'medium', 'ok'] as const).map((value) => {
                  const count = value === 'all' ? payload.summary.totalEndpoints : severityCounts[value] || 0;
                  const active = severityFilter === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setSeverityFilter(value)}
                      className={`${filterButtonClass} ${
                        active
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {value === 'all' ? 'All Severities' : value} ({count})
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setStatusFilter('all')}
                  className={`${filterButtonClass} ${
                    statusFilter === 'all'
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  All Statuses ({payload.summary.totalEndpoints})
                </button>
                <button
                  type="button"
                  onClick={() => setStatusFilter('ok')}
                  className={`${filterButtonClass} ${
                    statusFilter === 'ok'
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  ok ({statusCounts.ok || 0})
                </button>
                {statusFilterOptions.map((issueCode) => {
                  const active = statusFilter === issueCode;
                  return (
                    <button
                      key={issueCode}
                      type="button"
                      onClick={() => setStatusFilter(issueCode)}
                      className={`${filterButtonClass} ${
                        active
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {issueCode} ({statusCounts[issueCode] || 0})
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}

          {activeTab === 'types' ? (
            <>
              <div className="flex flex-wrap gap-2">
                {(['all', 'output', 'input'] as const).map((value) => {
                  const count =
                    value === 'all'
                      ? payload.summary.totalTypeFields || 0
                      : payload.typeFields.filter((field) => (value === 'input' ? field.input : !field.input)).length;
                  const active = typeModeFilter === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTypeModeFilter(value)}
                      className={`${filterButtonClass} ${
                        active
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {value === 'all' ? 'All Types' : value} ({count})
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2">
                {(['all', 'read', 'write'] as const).map((value) => {
                  const count = value === 'all' ? payload.summary.totalTypeFields || 0 : payload.summary.typeActionCounts[value] || 0;
                  const active = typeActionFilter === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTypeActionFilter(value)}
                      className={`${filterButtonClass} ${
                        active
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {value === 'all' ? 'All Actions' : value} ({count})
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2">
                {(['all', 'critical', 'high', 'medium', 'ok'] as const).map((value) => {
                  const count = value === 'all' ? payload.summary.totalTypeFields || 0 : typeSeverityCounts[value] || 0;
                  const active = typeSeverityFilter === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTypeSeverityFilter(value)}
                      className={`${filterButtonClass} ${
                        active
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {value === 'all' ? 'All Severities' : value} ({count})
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setTypeStatusFilter('all')}
                  className={`${filterButtonClass} ${
                    typeStatusFilter === 'all'
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  All Statuses ({payload.summary.totalTypeFields || 0})
                </button>
                <button
                  type="button"
                  onClick={() => setTypeStatusFilter('ok')}
                  className={`${filterButtonClass} ${
                    typeStatusFilter === 'ok'
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  ok ({typeStatusCounts.ok || 0})
                </button>
                {typeStatusFilterOptions.map((issueCode) => {
                  const active = typeStatusFilter === issueCode;
                  return (
                    <button
                      key={issueCode}
                      type="button"
                      onClick={() => setTypeStatusFilter(issueCode)}
                      className={`${filterButtonClass} ${
                        active
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {issueCode} ({typeStatusCounts[issueCode] || 0})
                    </button>
                  );
                })}
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'resolvers' | 'types' | 'policies')} className="w-full">
        <TabsList className={`grid w-full max-w-xl grid-cols-3 ${embedded ? 'text-xs' : ''}`}>
          <TabsTrigger value="resolvers" className={embedded ? 'text-xs' : ''}>Resolvers</TabsTrigger>
          <TabsTrigger value="types" className={embedded ? 'text-xs' : ''}>Types</TabsTrigger>
          <TabsTrigger value="policies" className={embedded ? 'text-xs' : ''}>Feature Map</TabsTrigger>
        </TabsList>

        <TabsContent value="resolvers">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 text-slate-900 ${embedded ? 'text-base' : ''}`}>
                <ShieldAlert className="h-5 w-5" />
                Resolver Audit
              </CardTitle>
              <CardDescription className={embedded ? 'text-[11px]' : ''}>
                {filteredEndpoints.length} matching resolvers.
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              <ScrollArea className="h-[70vh] w-full">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={tableHeaderClass}>Severity</TableHead>
                      <TableHead className={tableHeaderClass}>Status</TableHead>
                      <TableHead className={tableHeaderClass}>Resolver</TableHead>
                      <TableHead className={tableHeaderClass}>Current Feature</TableHead>
                      <TableHead className={tableHeaderClass}>Target</TableHead>
                      <TableHead className={tableHeaderClass}>File</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEndpoints.map((endpoint) => (
                      <TableRow key={`${endpoint.type}-${endpoint.name}-${endpoint.file}-${endpoint.line}`}>
                        <TableCell className="align-top">
                          <Badge variant="outline" className={`${severityBadgeClass(endpoint.highestSeverity)} ${detailBadgeClass}`}>
                            {endpoint.highestSeverity}
                          </Badge>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex max-w-xs flex-wrap gap-2">
                            {endpoint.issueCodes.length > 0 ? endpoint.issueCodes.map((issueCode) => (
                              <Badge
                                key={issueCode}
                                variant="outline"
                                className={`border-amber-200 bg-amber-50 text-amber-700 ${detailBadgeClass}`}
                              >
                                {issueCode}
                              </Badge>
                            )) : (
                              <Badge variant="outline" className={`border-emerald-200 bg-emerald-50 text-emerald-700 ${detailBadgeClass}`}>
                                ok
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className={`${kindBadgeClass(endpoint.type)} ${detailBadgeClass}`}>
                                {endpoint.type}
                              </Badge>
                              <span className={`font-medium text-slate-900 ${resolverNameClass}`}>{endpoint.name}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {!endpoint.mounted ? (
                                <Badge variant="outline" className={`${sourceOnlyBadgeClass()} ${detailBadgeClass}`}>
                                  source-only
                                </Badge>
                              ) : null}
                              {endpoint.unauthenticated ? (
                                <Badge variant="outline" className={`border-purple-200 bg-purple-50 text-purple-700 ${detailBadgeClass}`}>
                                  unauthenticated
                                </Badge>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-1">
                            {endpoint.policies.map((policy) => (
                              <code key={policy} className={codeClass}>
                                {policy}
                              </code>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-1">
                            {endpoint.recommendedPolicies.length > 0 ? endpoint.recommendedPolicies.map((policy) => (
                              <code key={policy} className={codeClass}>
                                {policy}
                              </code>
                            )) : (
                              <span className={embedded ? 'text-[11px] text-slate-500' : 'text-xs text-slate-500'}>—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <code className={fileCodeClass}>
                            {formatFileDisplay(endpoint.file, endpoint.line)}
                          </code>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="types">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 text-slate-900 ${embedded ? 'text-base' : ''}`}>
                <Shield className="h-5 w-5" />
                Type Field Audit
              </CardTitle>
              <CardDescription className={embedded ? 'text-[11px]' : ''}>
                {filteredTypeFields.length} matching type fields.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[70vh] rounded-md border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={tableHeaderClass}>Severity</TableHead>
                      <TableHead className={tableHeaderClass}>Status</TableHead>
                      <TableHead className={tableHeaderClass}>Field</TableHead>
                      <TableHead className={tableHeaderClass}>Current Feature</TableHead>
                      <TableHead className={tableHeaderClass}>Target</TableHead>
                      <TableHead className={tableHeaderClass}>File</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTypeFields.map((field) => (
                      <TableRow key={`${field.typeName}-${field.fieldName}-${field.file}-${field.line}`}>
                        <TableCell className="align-top">
                          <Badge variant="outline" className={`${severityBadgeClass(field.highestSeverity)} ${detailBadgeClass}`}>
                            {field.highestSeverity}
                          </Badge>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex max-w-xs flex-wrap gap-2">
                            {field.issueCodes.length > 0 ? field.issueCodes.map((issueCode) => (
                              <Badge
                                key={issueCode}
                                variant="outline"
                                className={`border-amber-200 bg-amber-50 text-amber-700 ${detailBadgeClass}`}
                              >
                                {issueCode}
                              </Badge>
                            )) : (
                              <Badge variant="outline" className={`border-emerald-200 bg-emerald-50 text-emerald-700 ${detailBadgeClass}`}>
                                ok
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className={`${typeModeBadgeClass(field.input)} ${detailBadgeClass}`}>
                                {field.input ? 'input' : 'output'}
                              </Badge>
                              <Badge variant="outline" className={`${kindBadgeClass(field.action === 'read' ? 'query' : 'mutation')} ${detailBadgeClass}`}>
                                {field.action}
                              </Badge>
                              <span className={`font-medium text-slate-900 ${resolverNameClass}`}>
                                {field.typeName}.{field.fieldName}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-1">
                            {field.policies.map((policy) => (
                              <code key={policy} className={codeClass}>
                                {policy}
                              </code>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="space-y-1">
                            {field.recommendedPolicies.length > 0 ? field.recommendedPolicies.map((policy) => (
                              <code key={policy} className={codeClass}>
                                {policy}
                              </code>
                            )) : (
                              <span className={embedded ? 'text-[11px] text-slate-500' : 'text-xs text-slate-500'}>—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <code className={fileCodeClass}>
                            {formatFileDisplay(field.file, field.line)}
                          </code>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="policies">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className={`text-slate-900 ${embedded ? 'text-base' : ''}`}>Feature to Function Map</CardTitle>
              <CardDescription className={embedded ? 'text-[11px]' : ''}>
                Read-only view of attached features and the resolvers currently carrying them.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[70vh] rounded-md border border-slate-200">
                <div className="space-y-4 p-4">
                  {filteredPolicyGroups.map((group) => (
                    <div key={group.policy} className="rounded-lg border border-slate-200 bg-white p-4">
                      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                        <div className="space-y-1">
                          <code className={`${embedded ? 'text-xs' : 'text-sm'} font-semibold text-slate-900`}>{group.policy}</code>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                              {group.endpointCount} endpoint{group.endpointCount === 1 ? '' : 's'}
                            </Badge>
                            <Badge variant="outline" className={issueBadgeClass(group.issueCount)}>
                              {group.issueCount} endpoint{group.issueCount === 1 ? '' : 's'} with issues
                            </Badge>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 rounded-md border border-slate-100">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className={tableHeaderClass}>Resolver</TableHead>
                              <TableHead className={tableHeaderClass}>Status</TableHead>
                              <TableHead className={tableHeaderClass}>File</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.endpoints.map((endpoint) => (
                              <TableRow key={`${group.policy}-${endpoint.type}-${endpoint.name}-${endpoint.file}-${endpoint.line}`}>
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <Badge variant="outline" className={kindBadgeClass(endpoint.type)}>
                                        {endpoint.type}
                                      </Badge>
                                      <span className={`font-medium text-slate-900 ${embedded ? 'text-xs' : ''}`}>{endpoint.name}</span>
                                    </div>
                                    {!endpoint.mounted ? (
                                      <Badge variant="outline" className={sourceOnlyBadgeClass()}>
                                        source-only
                                      </Badge>
                                    ) : null}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-2">
                                    {endpoint.issueCodes.length > 0 ? endpoint.issueCodes.map((issueCode) => (
                                      <Badge
                                        key={issueCode}
                                        variant="outline"
                                        className="border-amber-200 bg-amber-50 text-amber-700"
                                      >
                                        {issueCode}
                                      </Badge>
                                    )) : (
                                      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                                        ok
                                      </Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <code className={fileCodeClass}>
                                    {formatFileDisplay(endpoint.file, endpoint.line)}
                                  </code>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export const ResolverPolicyReportPage = () => {
  return <ResolverPolicyReportView />;
};
